import { clsx, type ClassValue } from "clsx";
import dayjs from "dayjs";
import { useTheme } from "next-themes";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(input: string | number): string {
  const date = new Date(input);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function absoluteUrl(path: string) {
  return `${process.env.NEXT_PUBLIC_APP_URL}${path}`;
}

export function useLogo() {
  const { resolvedTheme } = useTheme();

  const logoPerTheme: Record<string, string> = {
    light: "/ICON__TEXT_black.webp",
    dark: "/ICON__TEXT_white.webp",
  };

  return logoPerTheme[(resolvedTheme ?? "dark") as keyof typeof logoPerTheme];
}

export const formatToBaseTracifyDate = (dateString: string) => {
  // Handle MM/YYYY format
  if (/^\d{2}\/\d{4}$/.test(dateString)) {
    const [month, year] = dateString.split("/");
    return `${month}-${year}`;
  }

  // Handle DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split("/");
    return `${day}-${month}-${year}`;
  }

  // For other formats, use dayjs parsing
  const date = dayjs(
    dateString,
    ["YYYY-MM-DD", "DD-MM-YYYY", "MM/DD/YYYY"],
    true
  );

  if (date.isValid()) {
    return date.format("DD-MM-YYYY");
  }

  return dateString;
};

export function parseStoreUrl(url: string | undefined | null): string | null {
  if (!url) return null;

  try {
    // First check if it's a valid hostname
    const isHostname = new RegExp(
      "^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]).)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$"
    ).test(url);

    const hostname = isHostname ? url : new URL(url).hostname;
    const cleanHostname = hostname.replace(/^www\./, "");

    // Match 3rd level domains like .com.au, .co.uk etc
    const thirdLevelMatch = cleanHostname.match(/[^.]+\.[^.]+\.[^.]+$/);
    if (thirdLevelMatch) {
      return thirdLevelMatch[0];
    }

    // For regular domains, get the last two parts
    const parts = cleanHostname.split(".").reverse();
    if (parts.length >= 2) {
      return `${parts[1]}.${parts[0]}`;
    }
    return cleanHostname;
  } catch {
    return null;
  }
}

import { customAlphabet } from "nanoid";

const ALPHABET =
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ123456789";
const ID_LENGTH = 21;

export function generateId(): string {
  return customAlphabet(ALPHABET, ID_LENGTH)();
}
